import { Fragment, useState } from 'react';
import { Dialog, Transition, RadioGroup } from '@headlessui/react';
import {
  XMarkIcon,
  DocumentArrowDownIcon,
  TableCellsIcon,
  DocumentChartBarIcon,
  CalendarDaysIcon,
  ArrowDownTrayIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  farmId: string;
  farmName: string;
}

type ExportFormat = 'csv' | 'xlsx' | 'pdf';
type ExportType = 'indices' | 'weather' | 'alerts' | 'complete' | 'report';
type ReportType = 'farm_health' | 'seasonal_summary' | 'alert_history';

interface ExportOption {
  id: ExportType;
  name: string;
  description: string;
  icon: typeof DocumentArrowDownIcon;
  formats: ExportFormat[];
}

const exportOptions: ExportOption[] = [
  {
    id: 'indices',
    name: 'Vegetation Indices',
    description: 'NDVI, EVI, and other vegetation index data over time',
    icon: TableCellsIcon,
    formats: ['csv'],
  },
  {
    id: 'weather',
    name: 'Weather Data',
    description: 'Temperature, precipitation, humidity, and more',
    icon: CalendarDaysIcon,
    formats: ['csv'],
  },
  {
    id: 'alerts',
    name: 'Alert History',
    description: 'All alerts and their status',
    icon: DocumentArrowDownIcon,
    formats: ['csv'],
  },
  {
    id: 'complete',
    name: 'Complete Export',
    description: 'All farm data in a single JSON file',
    icon: DocumentChartBarIcon,
    formats: ['csv'],
  },
  {
    id: 'report',
    name: 'Generate Report',
    description: 'Create a comprehensive farm report',
    icon: DocumentChartBarIcon,
    formats: ['pdf'],
  },
];

const reportTypes: { id: ReportType; name: string; description: string }[] = [
  {
    id: 'farm_health',
    name: 'Farm Health Report',
    description: 'Current health status, NDVI trends, and recommendations',
  },
  {
    id: 'seasonal_summary',
    name: 'Seasonal Summary',
    description: 'Year-round analysis with monthly breakdowns',
  },
  {
    id: 'alert_history',
    name: 'Alert History Report',
    description: 'Complete alert history with statistics',
  },
];

export default function ExportModal({
  isOpen,
  onClose,
  farmId,
  farmName,
}: ExportModalProps) {
  const [selectedType, setSelectedType] = useState<ExportOption>(exportOptions[0]);
  const [selectedReportType, setSelectedReportType] = useState<ReportType>('farm_health');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 3);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);

    try {
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

      if (selectedType.id === 'report') {
        // Generate report
        const response = await axios.post(
          `${baseUrl}/api/v1/reports/generate`,
          {
            farmId,
            reportType: selectedReportType,
            dateRange: { start: startDate, end: endDate },
            format: 'pdf',
          },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );

        // Download the report data as JSON (PDF generation would require additional setup)
        const blob = new Blob([JSON.stringify(response.data.data, null, 2)], {
          type: 'application/json',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${farmName}_${selectedReportType}_report.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast.success('Report generated successfully!');
      } else if (selectedType.id === 'complete') {
        // Complete export - returns JSON
        const response = await axios.get(
          `${baseUrl}/api/v1/export/farm/${farmId}/complete`,
          {
            params: { startDate, endDate },
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          }
        );

        const blob = new Blob([JSON.stringify(response.data, null, 2)], {
          type: 'application/json',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${farmName}_complete_export.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast.success('Data exported successfully!');
      } else {
        // CSV export
        const response = await axios.get(
          `${baseUrl}/api/v1/export/farm/${farmId}/${selectedType.id}`,
          {
            params: { format: 'csv', startDate, endDate },
            responseType: 'blob',
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = `${farmName}_${selectedType.id}_${startDate}_${endDate}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast.success(`${selectedType.name} exported successfully!`);
      }

      onClose();
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    Export Data
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                  {/* Farm info */}
                  <div className="px-4 py-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Exporting data for</p>
                    <p className="font-medium text-gray-900">{farmName}</p>
                  </div>

                  {/* Export type selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      What would you like to export?
                    </label>
                    <RadioGroup value={selectedType} onChange={setSelectedType}>
                      <div className="space-y-2">
                        {exportOptions.map((option) => (
                          <RadioGroup.Option
                            key={option.id}
                            value={option}
                            className={({ checked }) =>
                              `relative flex cursor-pointer rounded-lg border px-4 py-3 focus:outline-none transition-colors ${
                                checked
                                  ? 'border-green-500 bg-green-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`
                            }
                          >
                            {({ checked }) => (
                              <>
                                <div className="flex w-full items-center justify-between">
                                  <div className="flex items-center">
                                    <div className={`flex-shrink-0 p-2 rounded-lg ${
                                      checked ? 'bg-green-100' : 'bg-gray-100'
                                    }`}>
                                      <option.icon
                                        className={`h-5 w-5 ${
                                          checked ? 'text-green-600' : 'text-gray-500'
                                        }`}
                                      />
                                    </div>
                                    <div className="ml-3">
                                      <RadioGroup.Label
                                        as="p"
                                        className={`text-sm font-medium ${
                                          checked ? 'text-green-900' : 'text-gray-900'
                                        }`}
                                      >
                                        {option.name}
                                      </RadioGroup.Label>
                                      <RadioGroup.Description
                                        as="span"
                                        className="text-xs text-gray-500"
                                      >
                                        {option.description}
                                      </RadioGroup.Description>
                                    </div>
                                  </div>
                                  {checked && (
                                    <CheckIcon className="h-5 w-5 text-green-600" />
                                  )}
                                </div>
                              </>
                            )}
                          </RadioGroup.Option>
                        ))}
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Report type selection (only for report export) */}
                  {selectedType.id === 'report' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Report Type
                      </label>
                      <select
                        value={selectedReportType}
                        onChange={(e) => setSelectedReportType(e.target.value as ReportType)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      >
                        {reportTypes.map((rt) => (
                          <option key={rt.id} value={rt.id}>
                            {rt.name}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        {reportTypes.find((rt) => rt.id === selectedReportType)?.description}
                      </p>
                    </div>
                  )}

                  {/* Date range */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Date Range
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          End Date
                        </label>
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleExport}
                    disabled={isExporting}
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isExporting ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                        Export
                      </>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
